import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import type { Location } from '@alexiarest/shared';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';

/** Pantallas que usan el local elegido arriba. */
export const LOCATION_ROUTES = new Set([
  '/',
  '/ingresos',
  '/gastos',
  '/analisis',
  '/resultados',
  '/tesoreria',
  '/inversiones',
  '/objetivos',
  '/impuestos',
  '/alexia',
  // Tu carta: las ventas por plato van por local, así que se ve y se elige uno.
  '/carta',
]);

/**
 * Donde tiene sentido «Todos los locales». Impuestos no: la periodificacion
 * CREA gastos, y cada uno tiene que caer en un local concreto (7 sep 2026).
 * Ventas sí (bloque C, 14 oct 2026): con varios locales marcados se ven
 * sumados y se anota de uno. Gastos e Inversiones también (fase Finanzas,
 * 14 oct 2026): se ven los de los locales marcados y lo nuevo va al primero
 * (o al que se elija en «¿De qué local?»).
 */
export const CONSOLIDATED_ROUTES = new Set(['/', '/ingresos', '/gastos', '/inversiones', '/analisis', '/resultados', '/tesoreria', '/objetivos', '/alexia']);

/** Pantallas donde se ve y se edita un local concreto. */
export function esRutaDeUnLocal(pathname: string): boolean {
  return LOCATION_ROUTES.has(pathname) && !CONSOLIDATED_ROUTES.has(pathname);
}

interface LocationState {
  locations: Location[];
  /** undefined = consolidado de todos los centros (o varios marcados: ver `ambito`). */
  locationId?: string;
  /**
   * Lo que se pide a la API en las lecturas (bloque C, 14 oct 2026):
   * undefined = todos, «id» = un local, «a,b» = esos locales.
   */
  ambito?: string;
  /** Ids de los locales que se están mirando (todos si no hay nada marcado). */
  idsMirados: string[];
  setLocationId: (locationId?: string) => void;
  /**
   * Locales marcados arriba («Local ▾», Alex 12 oct 2026). Vacío = todos.
   * Se guarda en el navegador y se mantiene al pasar del Panel a Finanzas.
   */
  seleccion: string[];
  setSeleccion: (ids: string[]) => void;
  /** Escrituras antiguas o hechas desde consolidado se asignan a Principal. */
  writeLocationId: string;
}

const LocationContext = createContext<LocationState | null>(null);

const CLAVE = 'alexia:locales';

function leerSeleccion(): string[] {
  try {
    const raw = localStorage.getItem(CLAVE);
    const valor: unknown = raw ? JSON.parse(raw) : null;
    if (Array.isArray(valor) && valor.every((v) => typeof v === 'string')) return valor as string[];
  } catch {
    /* sin almacenamiento: se empieza como siempre */
  }
  // Como antes del selector múltiple: se arranca en el local principal.
  return ['principal'];
}

/**
 * Lo que se pide a la API con lo marcado (bloque C): en las pantallas que
 * suman, todos (undefined), uno («id») o varios («a,b»); en las de un local,
 * siempre uno.
 */
export function ambitoDeSeleccion(seleccion: string[], locations: Location[], pathname: string): string | undefined {
  const ids = locations.map((l) => l.locationId);
  const validos = locations.length ? seleccion.filter((id) => ids.includes(id)) : seleccion;
  if (esRutaDeUnLocal(pathname)) return validos[0] ?? ids[0] ?? 'principal';
  if (!validos.length || (ids.length && validos.length >= ids.length)) return undefined;
  return validos.join(',');
}

/** Un solo local marcado (o la pantalla es de un local): ese. Varios o todos: undefined. */
export function localDeSeleccion(seleccion: string[], locations: Location[], pathname: string): string | undefined {
  const validos = locations.length ? seleccion.filter((id) => locations.some((l) => l.locationId === id)) : seleccion;
  if (esRutaDeUnLocal(pathname)) return validos[0] ?? locations[0]?.locationId ?? 'principal';
  if (validos.length === 1) return validos[0];
  return undefined;
}

export function LocationProvider({ children }: { children: ReactNode }): JSX.Element {
  const me = useQuery({ queryKey: ['me'], queryFn: () => api.getMe() });
  const { pathname } = useLocation();
  const locations = useMemo(
    () => (me.data?.locations ?? []).filter((location) => location.active),
    [me.data?.locations],
  );
  const [seleccion, setSeleccionState] = useState<string[]>(leerSeleccion);

  const setSeleccion = useCallback((ids: string[]) => {
    const unicos = [...new Set(ids)];
    setSeleccionState(unicos);
    try {
      localStorage.setItem(CLAVE, JSON.stringify(unicos));
    } catch {
      /* sin almacenamiento: se recuerda solo en esta visita */
    }
  }, []);
  const setLocationId = useCallback((id?: string) => setSeleccion(id ? [id] : []), [setSeleccion]);

  // Un local que ya no existe (o de otra cuenta en este navegador) no se queda marcado.
  useEffect(() => {
    if (!locations.length || !seleccion.length) return;
    const validos = seleccion.filter((id) => locations.some((l) => l.locationId === id));
    if (validos.length !== seleccion.length) setSeleccion(validos.length ? validos : [locations[0].locationId]);
  }, [locations, seleccion, setSeleccion]);

  const locationId = localDeSeleccion(seleccion, locations, pathname);
  const ambito = ambitoDeSeleccion(seleccion, locations, pathname);
  const idsMirados = useMemo(
    () => (ambito ? ambito.split(',') : locations.map((l) => l.locationId)),
    [ambito, locations],
  );

  const value = useMemo<LocationState>(
    () => ({
      locations,
      locationId,
      ambito,
      idsMirados,
      setLocationId,
      seleccion,
      setSeleccion,
      writeLocationId: locationId ?? idsMirados[0] ?? locations[0]?.locationId ?? 'principal',
    }),
    [ambito, idsMirados, locationId, locations, seleccion, setLocationId, setSeleccion],
  );
  return <LocationContext.Provider value={value}>{children}</LocationContext.Provider>;
}

export function useLocationScope(): LocationState {
  const value = useContext(LocationContext);
  if (!value) throw new Error('useLocationScope debe usarse dentro de <LocationProvider>.');
  return value;
}

/**
 * Los locales activos que se están mirando, con sus ajustes (días que cierra):
 * los avisos de calidad del Estado de resultados y de Análisis miran cada uno.
 */
export function useLocalesDelAmbito(): Location[] {
  const { locations, idsMirados } = useLocationScope();
  return useMemo(() => locations.filter((l) => l.active && idsMirados.includes(l.locationId)), [locations, idsMirados]);
}
